import { Link } from 'react-router-dom';
import { notificationKey } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon } from '@mypoz/ui';

import { usePackages } from '@/features/packages/index.js';
import { NotificationRow } from '@/features/notifications/index.js';
import { useNotifications } from '@/features/notifications/index.js';
import { resolveNotificationLink } from '@/features/notifications/index.js';
import type { DashboardNotification } from '@/features/notifications/index.js';

/**
 * The Dashboard's notification panel: its own heading and "view all" link live inside the card,
 * per the mentor design, rather than sitting above it as a page section.
 *
 * `notifications` still arrives as a prop (Dashboard.tsx's own top-5 slice) rather than read
 * straight from `useNotifications()` here — only `status` and `markRead` come from the hook
 * directly, since real loading/error states exist now that MYP-112 replaced the fixture, and a
 * top-5 slice of an empty "still loading" array would otherwise render as a false "no
 * notifications yet" for a moment on every page load.
 */
export function LatestNotifications({ notifications }: { notifications: DashboardNotification[] }) {
  const { t } = useTranslation();
  const { markRead, status } = useNotifications();
  // Passed to resolveNotificationLink, which no longer reaches packages on its own (MYP-40).
  const { packages } = usePackages();

  return (
    <Card className="p-0">
      <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4 sm:px-6">
        <h2 className="text-base font-semibold text-ink">{t('dashboard.notifications.title')}</h2>

        <Link
          to="/account/notifications"
          className="inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-brand transition-colors hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-0"
        >
          {t('dashboard.notifications.viewAll')}
          <Icon name="arrow_forward" size={17} />
        </Link>
      </div>

      {status === 'loading' ? (
        <div aria-hidden="true" className="grid gap-3 px-5 py-4 sm:px-6">
          <div className="h-4 w-full animate-pulse rounded bg-surface-alt" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-surface-alt" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-surface-alt" />
        </div>
      ) : status === 'error' ? (
        <p className="px-5 py-10 text-center text-sm text-muted sm:px-6">
          {t('dashboard.notifications.failed')}
        </p>
      ) : notifications.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-muted sm:px-6">
          {t('dashboard.notifications.empty')}
        </p>
      ) : (
        <div className="divide-y divide-line">
          {notifications.map((notification) => (
            <NotificationRow
              key={notificationKey(notification)}
              notification={notification}
              // This panel has no thread panel of its own to open in place, so an unresolved link
              // (see resolveNotificationLink) falls back to the full Notifications page, where it
              // can — same as every notification used to before MYP-67 added real package links.
              href={resolveNotificationLink(notification, packages) ?? '/account/notifications'}
              onSelect={() => markRead(notificationKey(notification))}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
